/**
 * Health → Planner synchronization (contract §13.3).
 *
 * Materializes planner items for a medication/supplement schedule inside a
 * bounded window (today + 13 days) — never unbounded rows. Dedupe key is
 * (sourceEntityId, date): re-saving a schedule updates FUTURE occurrences
 * in place and never creates duplicates. Items on past days are intake
 * history snapshots and are never touched.
 */
import type { MedicationSchedule, NewPlannerItem, PlannerItem } from '@/types'
import type { PlannerRepository } from '@/services/repositories/interfaces'
import { addLocalDays, computeOccurrenceDates } from '@/domain/recurrence'

/** Bounded materialization window: today plus 13 days ahead. */
export const PLANNER_SYNC_RANGE_DAYS = 13

/** Health planner entries are short moments, not time blocks. */
export const HEALTH_PLANNER_DURATION_MINUTES = 15

export const HEALTH_PLANNER_ACTIVITY_ID = 'health-schedule'
export const HEALTH_PLANNER_CATEGORY = 'Gezondheid'
export const HEALTH_PLANNER_ICON = 'pill'
export const HEALTH_PLANNER_COLOR = '#4e7291'

export type PlannerSyncResult = {
  created: number
  updated: number
  removed: number
}

function startMinuteOf(localTime: string): number {
  const [hours = '0', minutes = '0'] = localTime.split(':')
  return Number(hours) * 60 + Number(minutes)
}

function toPlannerDraft(schedule: MedicationSchedule, date: string): NewPlannerItem {
  const notes = schedule.instructions ?? undefined
  return {
    date,
    startMinute: startMinuteOf(schedule.localTime),
    durationMinutes: HEALTH_PLANNER_DURATION_MINUTES,
    activityId: HEALTH_PLANNER_ACTIVITY_ID,
    activityName: schedule.name,
    category: HEALTH_PLANNER_CATEGORY,
    color: HEALTH_PLANNER_COLOR,
    iconId: HEALTH_PLANNER_ICON,
    ...(notes ? { notes } : {}),
    source: 'health',
    sourceEntityId: schedule.id,
  }
}

function matchesSchedule(item: PlannerItem, scheduleId: string): boolean {
  return item.source === 'health' && item.sourceEntityId === scheduleId
}

function isUpToDate(item: PlannerItem, draft: NewPlannerItem): boolean {
  return (
    item.startMinute === draft.startMinute &&
    item.activityName === draft.activityName &&
    (item.notes ?? undefined) === draft.notes
  )
}

/**
 * Synchronizes one schedule into the planner for [today, today + 13 days].
 *
 * - Missing occurrences are created.
 * - Existing future occurrences that drifted (time, name, instructions) are
 *   updated in place — same id, no duplicates.
 * - Future occurrences that no longer match the rule are removed.
 * - Items dated before today are intake history: the read window starts at
 *   today, so they are never even loaded, let alone modified or removed.
 */
export async function syncScheduleToPlanner(
  planner: PlannerRepository,
  schedule: MedicationSchedule,
  today: string,
): Promise<PlannerSyncResult> {
  const rangeStart = today
  const rangeEnd = addLocalDays(today, PLANNER_SYNC_RANGE_DAYS)

  const occurrences = computeOccurrenceDates({
    startDate: schedule.startDate,
    endDate: schedule.endDate,
    localTime: schedule.localTime,
    rule: schedule.recurrenceRule,
    rangeStart,
    rangeEnd,
  })

  const existing = await planner.listForRange(rangeStart, rangeEnd)
  const own = existing.filter((item) => matchesSchedule(item, schedule.id))

  // First item per date wins; later same-date rows are stale duplicates and
  // are cleaned up below (future only).
  const byDate = new Map<string, PlannerItem>()
  const staleDuplicates: PlannerItem[] = []
  for (const item of own) {
    const current = byDate.get(item.date)
    if (!current) {
      byDate.set(item.date, item)
    } else if (item.date >= today) {
      staleDuplicates.push(item)
    }
  }

  const wantedDates = new Set<string>()
  const result: PlannerSyncResult = { created: 0, updated: 0, removed: 0 }

  for (const occurrence of occurrences) {
    // In-run dedupe guard: one planner item per schedule per date.
    if (wantedDates.has(occurrence.localDate)) continue
    wantedDates.add(occurrence.localDate)

    const draft = toPlannerDraft(schedule, occurrence.localDate)
    const current = byDate.get(occurrence.localDate)
    if (!current) {
      await planner.create(draft)
      result.created += 1
    } else if (!isUpToDate(current, draft)) {
      // Occurrence dates are always >= today (window starts today), so this
      // update can never touch intake history.
      await planner.update(current.id, {
        startMinute: draft.startMinute,
        activityName: draft.activityName,
        notes: draft.notes,
      })
      result.updated += 1
    }
  }

  for (const item of staleDuplicates) {
    await planner.remove(item.id)
    result.removed += 1
  }

  for (const item of own) {
    if (wantedDates.has(item.date) || staleDuplicates.includes(item)) continue
    // Rule changed: this future occurrence no longer exists. Everything in
    // `own` is within [today, today + 13 days], so history can't be hit.
    await planner.remove(item.id)
    result.removed += 1
  }

  return result
}

/**
 * Removes only the FUTURE planner items of a schedule (e.g. after the
 * schedule is deactivated). Past items remain as intake history.
 */
export async function removeFuturePlannerItemsForSchedule(
  planner: PlannerRepository,
  scheduleId: string,
  today: string,
): Promise<number> {
  const rangeEnd = addLocalDays(today, PLANNER_SYNC_RANGE_DAYS)
  const existing = await planner.listForRange(today, rangeEnd)
  const future = existing.filter(
    (item) => matchesSchedule(item, scheduleId) && item.date >= today,
  )
  for (const item of future) {
    await planner.remove(item.id)
  }
  return future.length
}
